import React from 'react';
import { Project } from '../../types';

export type TabKey =
  | 'overview' | 'tasks' | 'goals' | 'documents' | 'loop' | 'activity' | 'reports' | 'settings'
  | 'issues' | 'risks' | 'milestones' | 'decisions' | 'time' | 'budget' | 'resources' | 'changes'
  | 'sprints' | 'aiagent' | 'clientportal' | 'standup'
  // New Apr 19 module tabs.
  // 'submittalrfi' was previously two tabs ('rfp' + 'construction'); RFP
  // is now a tenant-level surface at /rfp, so the tab is consolidated
  // into one Submittal & RFI Tracking entry.
  | 'training' | 'punchlist' | 'stakeholders' | 'submittalrfi' | 'lessons' | 'forms';

interface Props {
  project: Project;
  active: TabKey;
  onChange: (t: TabKey) => void;
  onSearchOpen?: () => void;
  // When set (Stakeholder role), only these tab keys render. Null/undefined
  // means no restriction — the standard role behaviour for PM/TM/Observer.
  restrictTabs?: Set<TabKey> | null;
}

const ALWAYS: { key: TabKey; label: string }[] = [
  { key: 'overview',   label: 'Overview' },
  { key: 'tasks',      label: 'Tasks' },
  { key: 'decisions',  label: 'Decisions' },
  { key: 'documents',  label: 'Documents' },
  { key: 'forms',      label: 'Forms' },
  // Loop tab removed — Microsoft Loop has no public API for native in-app
  // integration (June 2026). The dagrun_pm_loopworkspaceid field + LoopTab
  // route are preserved for future use; the tab simply never renders.
  { key: 'activity',   label: 'Activity' },
  { key: 'reports',    label: 'Reports' },
  { key: 'settings',   label: 'Settings' },
];

export default function ProjectNavBar({ project, active, onChange, onSearchOpen, restrictTabs }: Props) {
  const moduleTabs: { key: TabKey; label: string; flag: keyof Project }[] = [
    { key: 'goals',        label: 'Goals',           flag: 'dagrun_pm_module_goals' },
    { key: 'issues',       label: 'Issues',          flag: 'dagrun_pm_module_issues' },
    { key: 'risks',        label: 'Risks',           flag: 'dagrun_pm_module_risks' },
    { key: 'milestones',   label: 'Milestones',      flag: 'dagrun_pm_module_milestones' },
    { key: 'time',         label: 'Time',            flag: 'dagrun_pm_module_timetracking' },
    { key: 'budget',       label: 'Budget',          flag: 'dagrun_pm_module_budget' },
    { key: 'resources',    label: 'Resources',       flag: 'dagrun_pm_module_resources' },
    { key: 'changes',      label: 'Change Requests', flag: 'dagrun_pm_module_changemanagement' },
    { key: 'sprints',      label: 'Sprints',         flag: 'dagrun_pm_module_agile' },
    { key: 'standup',      label: 'Standup',         flag: 'dagrun_pm_module_agile' },
    { key: 'aiagent',      label: 'AI Agent',        flag: 'dagrun_pm_module_ai' },
    { key: 'clientportal', label: 'Client Portal',   flag: 'dagrun_pm_module_clientportal' },
    { key: 'training',     label: 'Training',        flag: 'dagrun_pm_module_training' },
    { key: 'punchlist',    label: 'Punch List',      flag: 'dagrun_pm_module_punchlist' },
    { key: 'stakeholders', label: 'Stakeholders',    flag: 'dagrun_pm_module_stakeholders' },
    { key: 'submittalrfi', label: 'Submittal & RFI Tracking', flag: 'dagrun_pm_module_submittalrfi' },
    { key: 'lessons',      label: 'Lessons',         flag: 'dagrun_pm_module_lessons' },
  ];
  const enabledModules = moduleTabs.filter(m => Boolean(project[m.flag]));
  // Order: Overview, Tasks, Decisions (always), then enabled module tabs, then the
  // remaining "always-on" tabs (Documents/Loop/Activity/Reports/Settings).
  let visible = [ALWAYS[0], ALWAYS[1], ALWAYS[2], ...enabledModules, ...ALWAYS.slice(3)];
  // Stakeholder gating: only the per-stakeholder allowed tabs render. Overview
  // is always in the allowed set (baseline), so it is never hidden.
  if (restrictTabs) visible = visible.filter(t => restrictTabs.has(t.key));

  const tabBtn = (k: TabKey, label: string): React.ReactNode => {
    const isActive = active === k;
    return (
      <button
        key={k}
        onClick={() => onChange(k)}
        style={{
          background: 'transparent',
          border: 'none',
          borderBottom: `2px solid ${isActive ? '#0E7490' : 'transparent'}`,
          padding: '8px 12px',
          fontFamily: 'var(--font-ui)',
          fontSize: 14,
          fontWeight: isActive ? 500 : 400,
          color: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'color 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => !isActive && (e.currentTarget.style.color = 'rgba(255,255,255,0.85)')}
        onMouseLeave={e => !isActive && (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      background: '#111318',
      borderBottom: '0.5px solid #111318',
      padding: '0 24px',
      overflowX: 'auto',
      flexShrink: 0,
    }}>
      {visible.map(t => tabBtn(t.key, t.label))}
      <div style={{ flex: 1 }} />
      {onSearchOpen && (
        <button
          onClick={onSearchOpen}
          title="Search"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            padding: '6px 8px',
            fontSize: 14,
            alignSelf: 'center',
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
          onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}
        >
          🔍
        </button>
      )}
    </div>
  );
}
